class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.updatePreviewOffset();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Trap focus even when reduced-motion disables the opening transition.
    setTimeout(() => {
      this.classList.add('animate', 'active');
      trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.hydrateSmartRoutine();
    window.CsaCart?.enhance(this.querySelector('cart-drawer-items'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  updatePreviewOffset() {
    const bar = document.getElementById('PBarNextFrameWrapper');
    const measure = () => {
      const rect = bar?.getBoundingClientRect();
      const height = rect && rect.width > 0 && rect.height <= 160 && rect.bottom >= window.innerHeight - 1
        ? rect.height : 0;
      this.style.setProperty('--csa-preview-offset', `${height}px`);
    };
    measure();
    if (bar && this.previewBar !== bar && typeof ResizeObserver !== 'undefined') {
      this.previewObserver?.disconnect();
      this.previewBar = bar;
      this.previewObserver = new ResizeObserver(measure);
      this.previewObserver.observe(bar);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasOpen = this.classList.contains('active');
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    window.CsaCart?.enhance(this.querySelector('cart-drawer-items'));
    if (wasOpen) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
    this.hydrateSmartRoutine();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  hydrateSmartRoutine() {
    const routine = this.querySelector('[data-csa-smart-routine]');
    const list = routine?.querySelector('[data-csa-routine-list]');
    if (!routine || !list || routine.dataset.smartLoaded === 'true' || routine.dataset.smartLoaded === 'loading') return;

    const productIds = (routine.dataset.cartProductIds || '').split(',').filter(Boolean);
    const cartHandles = new Set((routine.dataset.cartProductHandles || '').split(',').filter(Boolean));
    const cartProductIds = new Set((routine.dataset.cartCoveredProductIds || routine.dataset.cartProductIds || '').split(',').filter(Boolean));
    if (!productIds.length) return;

    routine.dataset.smartLoaded = 'loading';
    const root = window.Shopify?.routes?.root || window.routes?.root_url || '/';
    const endpointRoot = root.endsWith('/') ? root : `${root}/`;
    const uniqueIds = [...new Set(productIds)].slice(0, 4);

    Promise.all(
      uniqueIds.map((productId) =>
        fetch(`${endpointRoot}recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    )
      .then((responses) => {
        const seenHandles = new Set(cartHandles);
        const recommendations = [];

        responses.forEach((response) => {
          (response?.products || []).forEach((product) => {
            if (!product?.handle || seenHandles.has(product.handle) || cartProductIds.has(String(product.id)) || product.available === false) return;
            const variant = this.getRecommendedProductVariant(product);
            if (!variant?.id) return;
            seenHandles.add(product.handle);
            recommendations.push({ product, variant });
          });
        });

        if (!recommendations.length) {
          routine.dataset.smartLoaded = 'fallback';
          return;
        }

        list.innerHTML = recommendations
          .slice(0, 2)
          .map(({ product, variant }) => this.renderSmartRoutineItem(product, variant, routine.dataset.currency || 'TRY'))
          .join('');
        routine.dataset.smartLoaded = 'true';
      })
      .catch(() => {
        routine.dataset.smartLoaded = 'fallback';
      });
  }

  getRecommendedProductVariant(product) {
    if (!Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available !== false) || product.variants[0];
  }

  renderSmartRoutineItem(product, variant, currency) {
    const title = this.escapeHtml(product.title || '');
    const localeRoot = window.Shopify?.routes?.root || '/';
    const productPath = product.url || `/products/${product.handle}`;
    const localizedPath = localeRoot !== '/' && productPath.startsWith('/') && !productPath.startsWith('//') && !productPath.startsWith(localeRoot)
      ? `${localeRoot.replace(/\/$/, '')}${productPath}` : productPath;
    const url = this.escapeHtml(localizedPath);
    const addLabel = this.escapeHtml((window.csaCommerceStrings?.smart_add_product || window.variantStrings?.addToCart || '')
      .split('[product]').join(product.title || ''));
    const image = this.escapeHtml(this.getRecommendedProductImage(product));
    const label = this.escapeHtml(this.getSmartRoutineLabel(product));
    const price = this.escapeHtml(this.formatSmartRoutineMoney(variant.price ?? product.price, currency));
    const variantId = this.escapeHtml(String(variant.id));

    return `
      <article class="csa-cart-drawer__routine-item" data-csa-smart-routine-item>
        <a href="${url}" class="csa-cart-drawer__routine-media" tabindex="-1" aria-hidden="true">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="180" height="180">` : ''}
        </a>
        <div class="csa-cart-drawer__routine-copy">
          <span>${label}</span>
          <a href="${url}">${title}</a>
          <p>${price}</p>
        </div>
        <product-form data-hide-errors="true">
          <form method="post" action="${window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="id" value="${variantId}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="csa-cart-drawer__routine-add" aria-label="${addLabel}">
              <span>+</span>
              <div class="loading__spinner hidden">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </div>
            </button>
          </form>
        </product-form>
      </article>
    `;
  }

  getRecommendedProductImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image?.url) return product.featured_image.url;
    if (Array.isArray(product.images) && product.images[0]) return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
    return '';
  }

  getSmartRoutineLabel(product) {
    const title = (product.title || '').toLocaleLowerCase('tr-TR');
    if (title.includes('güneş') || title.includes('sunscreen') || title.includes('spf')) return window.csaCommerceStrings?.smart_day || '';
    if (title.includes('barrier') || title.includes('bariyer')) return window.csaCommerceStrings?.smart_barrier || '';
    if (title.includes('reconstructor') || title.includes('peptid') || title.includes('serum')) return window.csaCommerceStrings?.smart_hydration || '';
    if (title.includes('yosun') || title.includes('foam') || title.includes('temizleyici')) return window.csaCommerceStrings?.smart_cleanse || '';
    if (title.includes('şampuan') || title.includes('saç') || title.includes('shampoo') || title.includes('hair')) return window.csaCommerceStrings?.smart_hair || '';
    return window.csaCommerceStrings?.smart_complement || '';
  }

  formatSmartRoutineMoney(value, currency) {
    const amount = Number(value || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
        style: 'currency',
        currency: currency || 'TRY',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      })
        .format(amount)
        .replace(/\s?₺/, 'TL');
    } catch (error) {
      return `${Math.round(amount).toLocaleString(document.documentElement.lang || 'tr-TR')} ${currency || 'TRY'}`;
    }
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[character]);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
